import { Box } from '@mui/material';
import { useAdminApi } from '@hooks/useAdminApi';
import { Typography } from '@mui/material';
import { Product } from '@protos/product';
import { useMemo, useState } from 'react';
import { useAsync } from 'react-use';
import { BaseEditDialog } from '@features/user-account/components/BaseEditDialog';
import { ReportLinksEditor } from './ReportLinksEditor';
import { FIELD_DEFAULT_VALUES, FIELD_TO_TITLE_MAP, PERIOD_OPTIONS, settlementPeriods, snapshotPeriods } from '../constants';
import { FreemiumNumberEditor, FreemiumSelectEditor, ProductSelectionEditor } from './FieldEditors';
import { FreemiumData, FreemiumDataResponse } from '../types';

interface CategoriesOptionsProps {
  isOpen: boolean;
  onClose: () => void;
  isPrivateAllowed: boolean;
  fieldsToShow: string[];
  category: string;
  freemiumData: FreemiumDataResponse | FreemiumData;
  title: string;
  handleSave?: (formChanges: Record<string, any>, category: string) => void;
  setRefreshDataKey?: (updater: (prev: number) => number) => void;
}

export const CategoriesOptions: React.FC<CategoriesOptionsProps> = ({
  isOpen,
  onClose,
  isPrivateAllowed = false,
  fieldsToShow = ['product_symbols'],
  category,
  freemiumData,
  title,
  handleSave,
  setRefreshDataKey,
}) => {
  const { apiClient } = useAdminApi();

  const originalData = useMemo(() => {
    if (!freemiumData || !category) {
      return FIELD_DEFAULT_VALUES;
    }

    const categoryData = Array.isArray(freemiumData) ? freemiumData.find(item => item.category === category)?.data : freemiumData;

    return {
      product_symbols: categoryData?.product_symbols || [],
      tenors: categoryData?.tenors || FIELD_DEFAULT_VALUES.tenors,
      delay: categoryData?.delay || FIELD_DEFAULT_VALUES.delay,
      range_days: categoryData?.range_days || FIELD_DEFAULT_VALUES.range_days,
      period: categoryData?.period || FIELD_DEFAULT_VALUES.period,
      snapshot_periods: categoryData?.snapshot_periods || FIELD_DEFAULT_VALUES.snapshot_periods,
      report_links: categoryData?.report_links || FIELD_DEFAULT_VALUES.report_links,
      report_link: categoryData?.report_link || FIELD_DEFAULT_VALUES.report_link,
      settlement_period: categoryData?.settlement_period || FIELD_DEFAULT_VALUES.settlement_period,
      products: categoryData?.products || FIELD_DEFAULT_VALUES.products,
    };
  }, [freemiumData, category]);

  const [userChanges, setUserChanges] = useState<FreemiumData>({});

  const currentFormData = useMemo(
    () => ({
      ...originalData,
      ...userChanges,
    }),
    [originalData, userChanges]
  );

  const products = useAsync(async () => {
    const products = await apiClient?.productsLoader({ tradable: true }).loadAllData();
    return {
      allSymbols: products || [],
      privateSymbols: products?.filter(product => product.private) || [],
    };
  }, [apiClient]);

  let nonPrivateProducts: Product[] = [];
  if (!products.loading && products.value) {
    nonPrivateProducts = products.value.allSymbols.filter(p => !products.value.privateSymbols.includes(p));
  }

  const allProductGroups = useMemo(() => {
    const groups: { [key: string]: Product[] } = {};
    if (products.loading || !products.value) return groups;
    const prodList = isPrivateAllowed ? products.value.allSymbols : nonPrivateProducts;

    prodList.forEach(product => {
      if (!groups[product.product_group]) {
        groups[product.product_group] = [];
      }
      groups[product.product_group].push(product);
    });
    return groups;
  }, [products, isPrivateAllowed]);

  const selectedProducts = useMemo(() => {
    if (products.loading || !products.value) return [];
    const prodList = isPrivateAllowed ? products.value.allSymbols : nonPrivateProducts;
    return prodList.filter(p => currentFormData.product_symbols?.some(symbol => symbol.toLowerCase() === p.symbol.toLowerCase()));
  }, [products, currentFormData, isPrivateAllowed]);

  const onFreemiumSave = async () => {
    const dataToSend = {};
    for (const key of fieldsToShow) {
      dataToSend[key] = currentFormData[key];
    }
    await apiClient?.updateFreemiumData(category, dataToSend);
    if (setRefreshDataKey) {
      setRefreshDataKey(prev => prev + 1);
    }
  };

  const onOfficialsSave = (handleSave: (formChanges: Record<string, any>, category: string) => void) => {
    handleSave(userChanges, category);
  };

  const onTradeLimitAdd = (product: Product) => {
    setUserChanges(prev => ({ ...prev, product_symbols: [...(currentFormData.product_symbols || []), product.symbol] }));
  };

  const onTradeLimitRemove = (tradeId: number) => {
    const updatedTrades = [...(currentFormData.product_symbols || [])];
    updatedTrades.splice(tradeId, 1);
    setUserChanges(prev => ({
      ...prev,
      product_symbols: currentFormData.product_symbols?.filter((symbol: string, index: number) => index !== tradeId) || [],
    }));
  };

  const onCheckboxChange = (product: Product) => {
    const ladderId = currentFormData.product_symbols?.findIndex(limit => limit === product.symbol) || -1;
    if (ladderId === -1) onTradeLimitAdd(product);
    else onTradeLimitRemove(ladderId);
  };

  const updateUserChanges = (update: Partial<FreemiumData>) => {
    setUserChanges(prev => ({ ...prev, ...update }));
  };

  if (products.loading || !products.value) return null;

  return (
    <BaseEditDialog
      title={`Edit ${title} Options`}
      isOpen={isOpen}
      onClose={onClose}
      clearErrorOn={[currentFormData.product_symbols]}
      defaultErrorMessage="There was an error updating the options"
      onSave={handleSave ? () => onOfficialsSave(handleSave) : onFreemiumSave}
      fullWidth={true}
      maxWidth="lg"
    >
      <Box display="flex" flexDirection="row" gap={1}>
        {fieldsToShow.includes('product_symbols') && (
          <ProductSelectionEditor
            allProductGroups={allProductGroups}
            selectedProducts={selectedProducts}
            onCheckboxChange={onCheckboxChange}
            onDelete={onTradeLimitRemove}
            isSelectionDisabled={() => (currentFormData.product_symbols?.length || 0) >= 10}
            symbols={currentFormData.product_symbols || []}
            maxProductsLimit={10}
            title="Product selection"
          />
        )}

        {/**
         * TODO: Will need to add a case for the officials product selection editor.
         */}
        <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2} width="100%">
          {fieldsToShow.includes('tenors') && (
            <FreemiumNumberEditor
              value={currentFormData.tenors || 0}
              max={24}
              min={1}
              onChange={(value: number) => updateUserChanges({ tenors: value })}
              title={FIELD_TO_TITLE_MAP['tenors']}
              label={FIELD_TO_TITLE_MAP['tenors']}
            />
          )}
          {fieldsToShow.includes('delay') && (
            <FreemiumNumberEditor
              value={currentFormData.delay || 0}
              max={180}
              min={0}
              onChange={(value: number) => updateUserChanges({ delay: value })}
              title={FIELD_TO_TITLE_MAP['delay']}
              label="Delay"
            />
          )}
          {fieldsToShow.includes('range_days') && (
            <FreemiumNumberEditor
              value={currentFormData.range_days || 0}
              max={365}
              min={0}
              onChange={(value: number) => updateUserChanges({ range_days: value })}
              title={FIELD_TO_TITLE_MAP['range_days']}
              label="Range"
            />
          )}
          {fieldsToShow.includes('snapshot_periods') && (
            <FreemiumSelectEditor
              value={currentFormData.snapshot_periods?.[0] || ''}
              options={Object.values(snapshotPeriods)}
              onChange={(value: string) => updateUserChanges({ snapshot_periods: [value] })}
              title={FIELD_TO_TITLE_MAP['snapshot_periods']}
            />
          )}
          {fieldsToShow.includes('period') && (
            <FreemiumSelectEditor
              value={currentFormData.period || ''}
              options={PERIOD_OPTIONS}
              onChange={(value: string) => updateUserChanges({ period: value })}
              title={FIELD_TO_TITLE_MAP['period']}
            />
          )}
          {fieldsToShow.includes('report_links') && (
            <Box display="flex" flexDirection="column" gap={2} width="100%">
              <Typography variant="h6">{FIELD_TO_TITLE_MAP['report_links']}</Typography>
              <ReportLinksEditor
                links={currentFormData.report_links || []}
                onChange={(links: string[]) => updateUserChanges({ report_links: links })}
                maxLinks={4}
              />
            </Box>
          )}
          {fieldsToShow.includes('report_link') && (
            <Box display="flex" flexDirection="column" gap={2} width="100%">
              <Typography variant="h6">{FIELD_TO_TITLE_MAP['report_link']}</Typography>
              <ReportLinksEditor
                links={currentFormData.report_link ? [currentFormData.report_link] : []}
                onChange={(links: string[]) => updateUserChanges({ report_link: links[0] })}
                maxLinks={1}
              />
            </Box>
          )}
          {fieldsToShow.includes('settlement_period') && (
            <FreemiumSelectEditor
              value={currentFormData.settlement_period || ''}
              options={Object.values(settlementPeriods)}
              onChange={(value: string) => updateUserChanges({ settlement_period: value })}
              title={FIELD_TO_TITLE_MAP['settlement_period']}
            />
          )}
        </Box>
      </Box>
    </BaseEditDialog>
  );
};
